import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye } from '@phosphor-icons/react';
import FlagIcons from './FlagIcons';
import { useAuth } from '../contexts/AuthContext';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import socket, { joinRoom } from '../utils/socket';
import AssignMBQuickPicker from './AssignMBQuickPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * `N` (New Members) header badge. Opens a dropdown (like PW/PT/G) listing
 * chats whose customer has no MB assigned yet — instead of navigating to a
 * full page. Each row → jump straight into that customer's chat, where the
 * MB can be assigned from the chat header. Auto-refreshes every 10s.
 */
const InstantAccessN = ({ variant = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, chats: [] });
  const [previewFor, setPreviewFor] = useState(null); // customer_id whose section list is open
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const load = useCallback(async (isCancelled = () => false) => {
    try {
      const res = await axios.get(`${API}/chats/new-members`, {
        params: { user_id: user?.id, limit: 200 },
      });
      if (!isCancelled()) {
        const d = { count: Number(res.data?.count || 0), chats: res.data?.chats || [] };
        setData(d);
        writeInstantCache('new_members_list', user?.id, d);
      }
    } catch (_) { /* transient */ }
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    const _cached = readInstantCache('new_members_list', user?.id);
    if (_cached && !cancelled) setData(_cached);
    load(() => cancelled);
    // N-Tab LIVE badge: join the new_members room and refetch the instant the
    // server pushes a new signup / New User arrival (no 10s poll lag). Keep a
    // slow 30s poll as a safety net.
    const doJoin = () => user?.id && joinRoom('new_members', user.id);
    doJoin();
    const onSignup = () => load(() => cancelled);
    socket.on('connect', doJoin);
    socket.on('new_member_signup', onSignup);
    const t = setInterval(() => { if (document.visibilityState === 'hidden') return; load(() => cancelled); }, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
      socket.off('connect', doJoin);
      socket.off('new_member_signup', onSignup);
    };
  }, [user?.id, load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // P3/P4 — only one of N/G/PT/PW open at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== 'N') setOpen(false); };
    window.addEventListener('dws:ia-open', onOther);
    return () => window.removeEventListener('dws:ia-open', onOther);
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('n_reopen') === '1') {
        sessionStorage.removeItem('n_reopen');
        setOpen(true);
      }
    } catch (_) {}
  }, []);

  const gotoChat = (c) => {
    if (!c?.room_id) return;
    try { sessionStorage.setItem('n_reopen', '1'); } catch (_) {}
    const path = window.location.pathname.includes('/handler') ? `/handler-chat/${c.room_id}` : `/admin-chat/${c.room_id}`;
    const chatTab = c.chat_type;
    navigate(path, { state: { activeTab: chatTab, chatTypeTab: chatTab } });
    setOpen(false);
  };

  // Point 10b — go to a SPECIFIC room (chat_type) for this customer.
  const gotoRoom = (room_id, chat_type) => {
    if (!room_id) return;
    try { sessionStorage.setItem('n_reopen', '1'); } catch (_) {}
    const path = window.location.pathname.includes('/handler') ? `/handler-chat/${room_id}` : `/admin-chat/${room_id}`;
    navigate(path, { state: { activeTab: chat_type, chatTypeTab: chat_type } });
    setOpen(false);
  };

  // Point 10b — eye click: 0 rooms → nothing; 1 room → open directly; >1 rooms
  // → show a small section list so the user picks which chat to open.
  const onEyeClick = (c) => {
    const rooms = (c.rooms || []).filter((r) => r.room_id);
    if (rooms.length === 0) return;
    if (rooms.length === 1) { gotoRoom(rooms[0].room_id, rooms[0].chat_type); return; }
    setPreviewFor(previewFor === c.customer_id ? null : c.customer_id);
  };

  const count = data.count || 0;
  const chats = data.chats || [];

  return (
    <div className="relative" ref={ref} data-testid="instant-access-n">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => { const nv = !v; if (nv) window.dispatchEvent(new CustomEvent('dws:ia-open', { detail: 'N' })); return nv; }); }}
        className={variant === 'light'
          ? "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-base shadow-md transition-colors"
          : "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-sky-400/25 hover:bg-sky-400/40 text-white font-black text-base border border-sky-200/60 shadow-inner transition-colors"}
        data-testid="instant-access-n-btn"
        title="New Members — chats with no MB assigned yet"
      >
        N
        {count > 0 && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow"
            data-testid="instant-access-n-total"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="fixed left-3 right-3 top-14 z-[80] sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-[340px] bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 max-h-[70vh] overflow-hidden flex flex-col"
          data-testid="n-menu"
        >
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span>New members — no MB yet</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/new-members'); }}
                className="text-[10px] font-bold text-sky-600 hover:text-sky-800 normal-case underline"
                data-testid="n-view-all-btn"
              >
                View all
              </button>
              <span className="text-sky-700 font-bold">{count}</span>
            </div>
          </div>
          <div className="overflow-y-auto">
            {chats.length === 0 ? (
              <div className="px-3 py-6 text-xs text-center text-slate-400">No new members ✓</div>
            ) : (
              chats.map((c) => (
                <div
                  key={c.customer_id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-sky-50"
                  data-testid={`n-item-${c.customer_id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-700 font-bold text-sm flex-shrink-0">
                    {(c.customer_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-[13px] truncate">{c.customer_name}</span>
                      {c.customer_flag && <FlagIcons flag={c.customer_flag} size={10} />}
                      {(c.chat_types || []).slice(0, 3).map((ct, i) => (
                        <span key={i} className="text-[9px] font-bold px-1 rounded bg-slate-100 text-slate-600 uppercase leading-none">{ct?.[0]}</span>
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{c.last_message || (c.room_id ? '(media)' : 'Signed up — no chat yet')}</div>
                  </div>
                  {(c.unread_count || 0) > 0 && (
                    <span className="min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0" data-testid={`n-unread-${c.customer_id}`}>
                      {c.unread_count > 99 ? '99+' : c.unread_count}
                    </span>
                  )}
                  {/* Phase C+Quick-Assign: MB picker straight from the N row —
                      ONLY for real customers with no MB yet (never New Users). */}
                  {!c.is_new_user && c.customer_id && !c.mb_setup && (
                    <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <AssignMBQuickPicker
                        customerId={c.customer_id}
                        currentMB={null}
                        actorId={user?.id}
                        actorRole={user?.role}
                        compact
                        onAssigned={() => load()}
                      />
                    </div>
                  )}
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); onEyeClick(c); }}
                      disabled={!(c.rooms || []).some((r) => r.room_id)}
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${(c.rooms || []).some((r) => r.room_id) ? 'bg-sky-500 hover:bg-sky-600 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                      title={(c.rooms || []).some((r) => r.room_id) ? 'Preview this customer\'s chats' : 'No chat yet'}
                      data-testid={`n-open-${c.customer_id}`}
                    >
                      <Eye size={12} weight="bold" />
                    </button>
                    {previewFor === c.customer_id && (
                      <div
                        className="absolute right-0 top-[calc(100%+4px)] z-[60] bg-white rounded-lg shadow-2xl border border-slate-200 w-40 py-1"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`n-preview-${c.customer_id}`}
                      >
                        {(c.rooms || []).filter((r) => r.room_id).map((r) => (
                          <button
                            key={r.room_id}
                            onClick={() => gotoRoom(r.room_id, r.chat_type)}
                            className="w-full flex items-center justify-between px-3 py-1.5 text-[12px] text-slate-700 hover:bg-sky-50 capitalize"
                            data-testid={`n-preview-${c.customer_id}-${r.chat_type}`}
                          >
                            <span>{r.chat_type}</span>
                            {(r.unread_count || 0) > 0 && (
                              <span className="min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                                {r.unread_count > 9 ? '9+' : r.unread_count}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantAccessN;
